import { useEffect, useState } from "react";
import { api, fileUrl, money, API } from "../lib/api";
import { PageHeader, Card, StatCard, EmptyState } from "../components/common";
import { EST_STAGES } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { Bank, CurrencyDollar, TrendUp, Clock, FileText, Wallet, Printer } from "@phosphor-icons/react";

const printStatement = (data) => {
  const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);
  const g = data.global;
  const rows = data.activities.map((a) => `
    <h3>${a.title} — Contrato ${fmt(a.amount)}${a.anticipo_percent > 0 ? ` · Anticipo ${a.anticipo_percent}% (${fmt(a.anticipo_amount)})` : ""}</h3>
    <table><thead><tr><th>#</th><th>Periodo</th><th>Bruto</th><th>Amort.</th><th>Reten.</th><th>Neto</th><th>Etapa</th></tr></thead><tbody>
    ${a.estimations.map((e) => `<tr><td>${e.number}</td><td>${e.period || "-"}</td><td>${fmt(e.gross)}</td><td>-${fmt(e.amortization)}</td><td>-${fmt(e.retencion || 0)}</td><td>${fmt(e.net)}</td><td>${e.status}</td></tr>`).join("")}
    </tbody><tfoot><tr><td colspan="2">Totales</td><td>${fmt(a.totals.gross)}</td><td>-${fmt(a.totals.amortized)}</td><td>-${fmt(a.totals.retenido || 0)}</td><td>${fmt(a.totals.net)}</td><td>Saldo: ${fmt(a.totals.saldo_contrato)}</td></tr></tfoot></table>`).join("");
  const html = `<html><head><title>Estado de cuenta — ${data.contractor.name}</title>
    <style>body{font-family:Arial,sans-serif;padding:30px;color:#0F172A}h1{margin:0}h3{margin:22px 0 6px;border-bottom:2px solid #FF5A00;padding-bottom:4px}
    table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:10px}th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}
    tfoot td{font-weight:bold;background:#f5f5f5}.sum{display:flex;gap:24px;margin:14px 0;font-size:13px}.sum b{display:block;font-size:16px}</style></head>
    <body><h1>Estado de cuenta</h1><p>${data.contractor.name} · ${new Date().toLocaleDateString("es-MX")}</p>
    <div class="sum">
      <div>Contratado<b>${fmt(g.contract_amount)}</b></div>
      <div>Anticipo<b>${fmt(g.anticipo)}</b></div>
      <div>Estimado bruto<b>${fmt(g.gross)}</b></div>
      <div>Fondo de garantía<b>${fmt(g.retenido || 0)}</b></div>
      <div>Pagado neto<b>${fmt(g.paid_net)}</b></div>
      <div>Por pagar<b>${fmt(g.por_pagar)}</b></div>
      <div>Saldo por ejercer<b>${fmt(g.saldo_contrato)}</b></div>
    </div>${rows}</body></html>`;
  const w = window.open("", "_blank");
  w.document.write(html); w.document.close();
  w.addEventListener("load", () => { try { w.print(); } catch (e) {} });
  setTimeout(() => { try { w.print(); } catch (e) {} }, 400);
};

const printGlobal = (data) => {
  const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);
  const g = data.global;
  const rows = data.contractors.map((c) => `<tr><td>${c.contractor.name}</td><td>${fmt(c.global.contract_amount)}</td><td>${fmt(c.global.gross)}</td><td>${fmt(c.global.paid_net)}</td><td>${fmt(c.global.por_pagar)}</td><td>${fmt(c.global.saldo_contrato)}</td></tr>`).join("");
  const html = `<html><head><title>Estado de cuenta — Toda la obra</title>
    <style>body{font-family:Arial,sans-serif;padding:30px;color:#0F172A}h1{margin:0}h3{margin:22px 0 6px;border-bottom:2px solid #FF5A00;padding-bottom:4px}
    table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px}th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}
    tfoot td{font-weight:bold;background:#f5f5f5}.sum{display:flex;flex-wrap:wrap;gap:24px;margin:14px 0;font-size:13px}.sum b{display:block;font-size:16px}</style></head>
    <body><h1>Estado de cuenta — Toda la obra</h1><p>${new Date().toLocaleDateString("es-MX")}</p>
    <div class="sum">
      <div>Contratado<b>${fmt(g.contract_amount)}</b></div>
      <div>Estimado bruto<b>${fmt(g.gross)}</b></div>
      <div>Fondo de garantía<b>${fmt(g.retenido || 0)}</b></div>
      <div>Pagado neto<b>${fmt(g.paid_net)}</b></div>
      <div>Por pagar<b>${fmt(g.por_pagar)}</b></div>
      <div>Saldo por ejercer<b>${fmt(g.saldo_contrato)}</b></div>
    </div>
    <h3>Subtotales por contratista</h3>
    <table><thead><tr><th>Contratista</th><th>Contratado</th><th>Estimado bruto</th><th>Pagado neto</th><th>Por pagar</th><th>Saldo por ejercer</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td>TOTAL</td><td>${fmt(g.contract_amount)}</td><td>${fmt(g.gross)}</td><td>${fmt(g.paid_net)}</td><td>${fmt(g.por_pagar)}</td><td>${fmt(g.saldo_contrato)}</td></tr></tfoot>
    </table></body></html>`;
  const w = window.open("", "_blank");
  w.document.write(html); w.document.close();
  w.addEventListener("load", () => { try { w.print(); } catch (e) {} });
  setTimeout(() => { try { w.print(); } catch (e) {} }, 400);
};

export default function EstadoCuenta() {
  const { user } = useAuth();
  const [contractors, setContractors] = useState([]);
  const [selected, setSelected] = useState("__all__");
  const [data, setData] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  const [pc, setPc] = useState({ start: "", end: "", data: null, busy: false });
  const loadPC = async () => {
    setPc((p) => ({ ...p, busy: true }));
    try {
      const r = await api.get(`/payments-consolidated`, { params: { start: pc.start || undefined, end: pc.end || undefined } });
      setPc((p) => ({ ...p, data: r.data, busy: false }));
    } catch { setPc((p) => ({ ...p, busy: false })); }
  };

  useEffect(() => {
    api.get("/contractors").then((r) => {
      setContractors(r.data);
      if (user?.role === "contratista") setSelected(user.contractor_id || "");
    });
  }, [user]);

  useEffect(() => {
    if (selected === "__all__") {
      api.get("/statement-global").then((r) => { setGlobalData(r.data); setData(null); }).catch(() => setGlobalData(null));
    } else if (selected) {
      api.get(`/statement/${selected}`).then((r) => { setData(r.data); setGlobalData(null); }).catch(() => setData(null));
    }
  }, [selected]);

  return (
    <div>
      <PageHeader overline="Finanzas por contratista" title="Estado de cuenta"
        action={
          <div className="flex items-center gap-2">
            <select data-testid="statement-contractor" className="h-11 px-3 rounded-sm border border-input bg-white text-sm min-w-[200px]" value={selected} onChange={(e) => setSelected(e.target.value)}>
              {user?.role !== "contratista" && <option value="__all__">Toda la obra (todos los contratistas)</option>}
              <option value="">Selecciona contratista…</option>
              {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {data && (
              <button onClick={() => printStatement(data)} data-testid="print-statement-btn"
                className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors duration-200">
                <Printer size={18} weight="bold" /> Imprimir / PDF
              </button>
            )}
            {globalData && (
              <button onClick={() => printGlobal(globalData)} data-testid="print-global-btn"
                className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors duration-200">
                <Printer size={18} weight="bold" /> Imprimir / PDF
              </button>
            )}
          </div>
        } />

      {selected === "__all__" ? (
        !globalData ? (
          <EmptyState icon={Bank} title="Cargando resumen de la obra…" subtitle="Resumen financiero global de todos los contratistas." />
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <StatCard label="Contratado (obra)" value={money(globalData.global.contract_amount)} icon={CurrencyDollar} />
              <StatCard label="Anticipo" value={money(globalData.global.anticipo)} icon={Wallet} tone="orange" />
              <StatCard label="Pagado (neto)" value={money(globalData.global.paid_net)} icon={TrendUp} tone="green" />
              <StatCard label="Por pagar" value={money(globalData.global.por_pagar)} icon={Clock} tone={globalData.global.por_pagar ? "orange" : "default"} />
            </div>
            <Card className="p-5 mb-8">
              <div className="overline mb-3">Resumen global · Toda la obra</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
                <div><div className="text-muted-foreground">Estimado bruto</div><div className="font-heading font-bold text-lg">{money(globalData.global.gross)}</div></div>
                <div><div className="text-muted-foreground">Amortizado</div><div className="font-heading font-bold text-lg">-{money(globalData.global.amortized)}</div></div>
                <div><div className="text-muted-foreground">Fondo garantía</div><div className="font-heading font-bold text-lg text-amber-600">{money(globalData.global.retenido || 0)}</div></div>
                <div><div className="text-muted-foreground">Neto estimado</div><div className="font-heading font-bold text-lg text-primary">{money(globalData.global.net)}</div></div>
                <div><div className="text-muted-foreground">Pagado neto</div><div className="font-heading font-bold text-lg text-success">{money(globalData.global.paid_net)}</div></div>
                <div><div className="text-muted-foreground">Por pagar</div><div className="font-heading font-bold text-lg">{money(globalData.global.por_pagar)}</div></div>
                <div><div className="text-muted-foreground">Saldo por ejercer</div><div className="font-heading font-bold text-lg">{money(globalData.global.saldo_contrato)}</div></div>
              </div>
            </Card>
            <div className="space-y-3">
              <div className="overline">Resumen por contratista</div>
              <Card className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-border text-left">{["Contratista", "Contratado", "Estimado bruto", "Pagado neto", "Por pagar", "Saldo por ejercer"].map((h) => <th key={h} className="px-4 py-3 overline">{h}</th>)}</tr></thead>
                  <tbody>
                    {globalData.contractors.map((c) => (
                      <tr key={c.contractor.id} data-testid={`global-row-${c.contractor.id}`} onClick={() => setSelected(c.contractor.id)} className="border-b border-border last:border-0 hover:bg-muted/50 cursor-pointer transition-colors duration-150">
                        <td className="px-4 py-3 font-semibold">{c.contractor.name}</td>
                        <td className="px-4 py-3">{money(c.global.contract_amount)}</td>
                        <td className="px-4 py-3">{money(c.global.gross)}</td>
                        <td className="px-4 py-3 text-success font-semibold">{money(c.global.paid_net)}</td>
                        <td className="px-4 py-3 text-primary">{money(c.global.por_pagar)}</td>
                        <td className="px-4 py-3">{money(c.global.saldo_contrato)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
            <Card className="p-5 mt-8" data-testid="consolidated-payments">
              <div className="overline mb-3">Pagos consolidados por contratista</div>
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div><label className="text-xs font-semibold text-muted-foreground block mb-1">Desde</label><input type="date" data-testid="pc-start" className="h-10 px-3 rounded-sm border border-input bg-white text-sm" value={pc.start} onChange={(e) => setPc({ ...pc, start: e.target.value })} /></div>
                <div><label className="text-xs font-semibold text-muted-foreground block mb-1">Hasta</label><input type="date" data-testid="pc-end" className="h-10 px-3 rounded-sm border border-input bg-white text-sm" value={pc.end} onChange={(e) => setPc({ ...pc, end: e.target.value })} /></div>
                <button onClick={loadPC} data-testid="pc-load-btn" disabled={pc.busy} className="h-10 px-4 rounded-sm bg-primary text-white text-sm font-semibold hover:opacity-90">{pc.busy ? "Consultando…" : "Consultar"}</button>
                {pc.data && <a href={`${API}/payments-consolidated/excel?start=${pc.start}&end=${pc.end}`} data-testid="pc-excel-btn" className="h-10 inline-flex items-center gap-1.5 px-4 rounded-sm border border-input text-sm font-medium hover:bg-muted"><FileText size={16} weight="bold" /> Exportar Excel</a>}
              </div>
              {!pc.data ? (
                <p className="text-sm text-muted-foreground">Elige un rango de fechas (opcional) y pulsa Consultar para ver el total neto pagado a cada contratista en el periodo.</p>
              ) : pc.data.groups.length === 0 ? (
                <p className="text-sm text-muted-foreground">No hay pagos registrados en ese periodo.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border text-left">{["Contratista", "# Pagos", "Total neto pagado"].map((h) => <th key={h} className="px-4 py-2 overline">{h}</th>)}</tr></thead>
                    <tbody>
                      {pc.data.groups.map((g) => (
                        <tr key={g.contractor_id || "sin"} data-testid={`pc-row-${g.contractor_id || "sin"}`} className="border-b border-border last:border-0">
                          <td className="px-4 py-2 font-semibold">{g.contractor_name}</td>
                          <td className="px-4 py-2">{g.count}</td>
                          <td className="px-4 py-2 text-success font-semibold">{money(g.total_net)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="font-semibold bg-muted/40"><td className="px-4 py-2">TOTAL</td><td className="px-4 py-2">{pc.data.groups.reduce((a, g) => a + g.count, 0)}</td><td className="px-4 py-2 text-success">{money(pc.data.grand_total)}</td></tr></tfoot>
                  </table>
                </div>
              )}
            </Card>
          </>
        )
      ) : !data ? (
        <EmptyState icon={Bank} title="Selecciona un contratista" subtitle="Verás el resumen global y el desglose por actividad (contrato)." />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatCard label="Contratado" value={money(data.global.contract_amount)} icon={CurrencyDollar} />
            <StatCard label="Anticipo" value={money(data.global.anticipo)} icon={Wallet} tone="orange" />
            <StatCard label="Pagado (neto)" value={money(data.global.paid_net)} icon={TrendUp} tone="green" />
            <StatCard label="Por pagar" value={money(data.global.por_pagar)} icon={Clock} tone={data.global.por_pagar ? "orange" : "default"} />
          </div>

          <Card className="p-5 mb-8">
            <div className="overline mb-3">Resumen global · {data.contractor.name}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
              <div><div className="text-muted-foreground">Estimado bruto</div><div className="font-heading font-bold text-lg">{money(data.global.gross)}</div></div>
              <div><div className="text-muted-foreground">Amortizado</div><div className="font-heading font-bold text-lg">-{money(data.global.amortized)}</div></div>
              <div><div className="text-muted-foreground">Fondo garantía</div><div className="font-heading font-bold text-lg text-amber-600">{money(data.global.retenido || 0)}</div></div>
              <div><div className="text-muted-foreground">Neto estimado</div><div className="font-heading font-bold text-lg text-primary">{money(data.global.net)}</div></div>
              <div><div className="text-muted-foreground">Pagado neto</div><div className="font-heading font-bold text-lg text-success">{money(data.global.paid_net)}</div></div>
              <div><div className="text-muted-foreground">Por pagar</div><div className="font-heading font-bold text-lg">{money(data.global.por_pagar)}</div></div>
              <div><div className="text-muted-foreground">Saldo por ejercer</div><div className="font-heading font-bold text-lg">{money(data.global.saldo_contrato)}</div></div>
            </div>
          </Card>

          <Card className="p-5 mb-8">
            <div className="overline mb-4">Avance financiero por actividad</div>
            {data.activities.filter((a) => a.totals.net > 0).length === 0 ? (
              <p className="text-sm text-muted-foreground">Aún no hay estimaciones para graficar.</p>
            ) : (
              <div className="space-y-3">
                {data.activities.filter((a) => a.totals.net > 0).map((a) => {
                  const pct = a.totals.net ? Math.round((a.totals.paid_net / a.totals.net) * 100) : 0;
                  return (
                    <div key={a.contract_id} data-testid={`chart-${a.contract_id}`}>
                      <div className="flex justify-between text-sm mb-1"><span className="font-medium truncate">{a.title}</span><span className="text-muted-foreground">{money(a.totals.paid_net)} / {money(a.totals.net)} ({pct}%)</span></div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden flex">
                        <div className="h-full bg-success transition-all duration-500" style={{ width: `${pct}%` }} />
                        <div className="h-full bg-primary/60 transition-all duration-500" style={{ width: `${a.totals.net ? Math.round((a.totals.por_pagar / a.totals.net) * 100) : 0}%` }} />
                      </div>
                    </div>
                  );
                })}
                <div className="flex gap-4 text-xs text-muted-foreground pt-1"><span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-success inline-block" /> Pagado</span><span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-primary/60 inline-block" /> Por pagar</span></div>
              </div>
            )}
          </Card>

          {data.activities.length === 0 ? (
            <EmptyState icon={FileText} title="Sin contratos" subtitle="Este contratista no tiene contratos registrados." />
          ) : (
            <div className="space-y-6">
              <div className="overline">Desglose por actividad</div>
              {data.activities.map((a) => (
                <Card key={a.contract_id} data-testid={`statement-activity-${a.contract_id}`} className="p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div>
                      <div className="font-heading font-bold text-lg">{a.title}</div>
                      <div className="text-sm text-muted-foreground">Contrato {money(a.amount)}{a.anticipo_percent > 0 ? ` · Anticipo ${a.anticipo_percent}% (${money(a.anticipo_amount)})` : ""}</div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-muted-foreground">Pagado / Por pagar</div>
                      <div className="font-heading font-bold"><span className="text-success">{money(a.totals.paid_net)}</span> / <span className="text-primary">{money(a.totals.por_pagar)}</span></div>
                    </div>
                  </div>

                  {a.estimations.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sin estimaciones.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-left">
                            {["#", "Periodo", "Bruto", "Amort.", "Reten.", "Neto", "Etapa", "Comprobante"].map((h) => <th key={h} className="px-3 py-2 overline">{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {a.estimations.map((e) => (
                            <tr key={e.id} className="border-b border-border last:border-0">
                              <td className="px-3 py-2 font-semibold">{e.number}</td>
                              <td className="px-3 py-2 text-muted-foreground">{e.period || "—"}</td>
                              <td className="px-3 py-2">{money(e.gross)}</td>
                              <td className="px-3 py-2 text-muted-foreground">-{money(e.amortization)}</td>
                              <td className="px-3 py-2 text-amber-600">-{money(e.retencion || 0)}</td>
                              <td className="px-3 py-2 font-semibold text-primary">{money(e.net)}</td>
                              <td className="px-3 py-2"><span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-semibold ${(EST_STAGES[e.status] || {}).tone || "bg-slate-100"}`}>{(EST_STAGES[e.status] || {}).short || e.status}</span></td>
                              <td className="px-3 py-2">{e.receipt ? <a href={fileUrl(e.receipt)} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 hover:underline"><FileText size={15} weight="fill" /> Ver</a> : <span className="text-muted-foreground">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="font-semibold bg-muted/40">
                            <td className="px-3 py-2" colSpan={2}>Totales</td>
                            <td className="px-3 py-2">{money(a.totals.gross)}</td>
                            <td className="px-3 py-2">-{money(a.totals.amortized)}</td>
                            <td className="px-3 py-2 text-amber-600">-{money(a.totals.retenido || 0)}</td>
                            <td className="px-3 py-2 text-primary">{money(a.totals.net)}</td>
                            <td className="px-3 py-2" colSpan={2}>Saldo por ejercer: {money(a.totals.saldo_contrato)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
